import clsx from 'clsx';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { translateEventToTicketGroupAccordionItem } from 'src/components/Accordions/TicketGroupsEventsAccordion';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { TabItem } from 'src/components/Events/EventPage/EventPageTabs';
import { SidePanel } from 'src/components/Events/EventPage/SidePanel/SidePanel';
import { ActiveFocusContextProvider } from 'src/contexts/ActiveFocusContext/ActiveFocusContext';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { CollapsableViewProvider } from 'src/contexts/CollapsableViewContext/CollapsableViewContext';
import { Content } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import {
  SidePanelPage,
  SidePanelProvider,
} from 'src/contexts/SidePanelContext';
import { ExplorerLayout } from 'src/core/layouts';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useQuickFilterSearchParam } from 'src/hooks/useQuickFilters';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { DeliveryIcon, PurchaseIcon, TagNewIcon } from 'src/svgs';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { PurchaseDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { getDeepLinkIdFromUrl } from 'src/utils/deepLinkUtils';
import { getAppliedFilterCounts } from 'src/utils/eventQueryUtils';
import { getPurchaseOrderDetailsModalConfigWithDeepLink } from 'src/utils/purchaseUtils';
import {
  EventWithData,
  PurchaseOrderDetails,
  PurchaseOrderQuery,
  PurchaseOrderTicketGroup,
  UserSetting
} from 'src/WebApiController';
import Swiper from 'swiper';

import * as styles from '../../Listings/InventoryEventPage/InventoryEventPage.css';
import { TagsSection } from '../../Listings/InventoryEventPage/Sections/TagsSection';
import { PurchaseEventFulfillmentSection } from './PurchaseEventFulfillmentSection';
import { PurchaseEventInfoSection } from './PurchaseEventInfoSection';
import { PurchaseEventPageHeaderV2 } from './PurchaseEventPageHeaderV2/PurchaseEventPageHeaderV2';

export function PurchaseEventPage({
  viagVirtId,
  posEventIds,
  returnUrl,
}: {
  posEventIds?: string[];
  viagVirtId?: string;
  returnUrl?: string;
}) {
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    eventsTransformed,
    eventsExpansion: { setListItemExpansion },
  } = useCatalogDataContext();

  useEffect(() => {
    if (!viagVirtId && posEventIds?.length) {
      setListItemExpansion(
        true,
        posEventIds.map((id) => `old:${id}`)
      );
    } else if (viagVirtId) {
      setListItemExpansion(true, [viagVirtId]);
    }
  }, [
    activeAccountWebClientConfig,
    posEventIds,
    setListItemExpansion,
    viagVirtId,
  ]);

  const { allGroupsIds, setAllGroupsIds } = useMultiSelectionContext();

  const accordionItemProps = useMemo(
    () =>
      eventsTransformed?.length
        ? eventsTransformed.find(
            (ev) =>
              ev.event.virtId === viagVirtId ||
              ev.event.posIds.some((id) => posEventIds?.includes(id))
          )
        : undefined,
    [eventsTransformed, posEventIds, viagVirtId]
  );

  const purchaseOrderAccordionItem = useMemo(
    () =>
      accordionItemProps &&
      translateEventToTicketGroupAccordionItem(accordionItemProps, 0, true),
    [accordionItemProps]
  );

  useEffect(() => {
    // Important useEffect to keep allGroupIds in sync with the items
    // This is important for the multi select to work properly
    if (
      purchaseOrderAccordionItem &&
      !isEqual(allGroupsIds, [purchaseOrderAccordionItem.itemId])
    ) {
      setAllGroupsIds([purchaseOrderAccordionItem.itemId]);
    }
  }, [allGroupsIds, purchaseOrderAccordionItem, setAllGroupsIds]);

  return (
    <SidePanelProvider sidePanelId="purchase-event">
      <ActiveFocusContextProvider>
        <Stack direction="column" height="full">
          <PurchaseEventContent
            accordionItemProps={accordionItemProps}
            purchaseOrderAccordionItem={purchaseOrderAccordionItem}
            returnUrl={returnUrl}
          />
        </Stack>
      </ActiveFocusContextProvider>
    </SidePanelProvider>
  );
}

type PurchaseEventContentProps = {
  accordionItemProps?: EventWithData;
  purchaseOrderAccordionItem?: EventAccordionItemProps<PurchaseOrderTicketGroup>;
  returnUrl?: string;
};

export const enum PurchaseEventTabs {
  Purchases = 'purchases',
  Tags = 'tags',
  Fulfillment = 'fulfillment',
  None = 'none',
}

function PurchaseEventContent({
  accordionItemProps,
  purchaseOrderAccordionItem,
  returnUrl,
}: PurchaseEventContentProps) {
  const intialActiveTab = PurchaseEventTabs.Purchases;

  const [selectedPurchaseOrderIds, setSelectedPurchaseOrderIds] = useState<
    number[]
  >([]);
  const [activeTab, setActiveTab] = useState(intialActiveTab);

  const { isLoading } = useCatalogDataContext();
  const isMobile = useMatchMedia('mobile');

  const quickFilterSearchParams = useQuickFilterSearchParam();
  const { tempQuery, initialQuery, filterQuery } =
    useFilterQueryContext<PurchaseOrderQuery>();

  const { setModal } = useContext(ModalContext);
  const { posEntity: purchaseOrder, setActivePosEntity } =
    useActivePosEntityContext<PurchaseOrderDetails>();

  const {
    value: defaultPurchasesTabUserSetting,
    setUserSetting: setDefaultPurchasesTabUserSetting,
    isLoading: defaultPurchasesTabLoading,
  } = useServerUserSetting<PurchaseEventTabs>({
    id: UserSetting.PurchasesDefaultTab,
  });

  const activeId = getDeepLinkIdFromUrl(
    PurchaseDeeplinkQueryParam,
    window.location.href
  );
  useEffect(() => {
    if (activeId?.length && !purchaseOrder) {
      setActivePosEntity(parseInt(activeId), undefined, true);
      setModal(getPurchaseOrderDetailsModalConfigWithDeepLink(activeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const [defaultTab, setDefaultTab] = useState<PurchaseEventTabs | undefined>(
    defaultPurchasesTabUserSetting
  );

  useEffect(() => {
    if (
      defaultPurchasesTabUserSetting != null &&
      defaultPurchasesTabUserSetting !== activeTab &&
      defaultPurchasesTabUserSetting !== PurchaseEventTabs.None &&
      defaultPurchasesTabLoading === false
    ) {
      setActiveTab(defaultPurchasesTabUserSetting);
    }
    if (
      defaultPurchasesTabUserSetting != null &&
      defaultPurchasesTabUserSetting !== defaultTab &&
      defaultPurchasesTabUserSetting !== PurchaseEventTabs.None &&
      defaultPurchasesTabLoading === false
    ) {
      setDefaultTab(defaultPurchasesTabUserSetting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPurchasesTabLoading, defaultPurchasesTabUserSetting]);

  const onSetDefaultTabClick = useCallback(
    async (tab: PurchaseEventTabs) => {
      if (tab !== activeTab && tab !== PurchaseEventTabs.None) {
        setActiveTab(tab);
      }
      if (tab !== defaultTab) {
        setDefaultTab(tab);
      }
      setDefaultPurchasesTabUserSetting(tab);
    },
    [activeTab, defaultTab, setDefaultPurchasesTabUserSetting]
  );

  const swiperRef = useRef<Swiper>();

  const event = accordionItemProps?.event;

  const poIds = useMemo(() => {
    return (
      purchaseOrderAccordionItem?.entities?.map((tg) => String(tg.id)) || []
    );
  }, [purchaseOrderAccordionItem?.entities]);

  const hasConsignorMasterUI = useUserHasConsignorMasterUI();

  const tabs: TabItem<PurchaseEventTabs>[] = [
    {
      value: PurchaseEventTabs.Purchases,
      label: <Content id={ContentId.Purchases} />,
      contentId: ContentId.Purchases,
      content: (
        <PurchaseEventInfoSection
          accordionItemProps={accordionItemProps}
          purchaseOrderAccordionItem={purchaseOrderAccordionItem}
          selectedPurchaseOrderIds={selectedPurchaseOrderIds}
          setSelectedPurchaseOrderIds={setSelectedPurchaseOrderIds}
        />
      ),
      Icon: PurchaseIcon,
      className: styles.tabContent,
    },
  ];

  if (event?.virtId && !hasConsignorMasterUI) {
    tabs.push({
      value: PurchaseEventTabs.Tags,
      label: <Content id={ContentId.Tags} />,
      contentId: ContentId.Tags,
      content: <TagsSection eventId={event.virtId} />,
      Icon: TagNewIcon,
      useStroke: true,
    });
  }

  if (event?.viagId) {
    tabs.push({
      value: PurchaseEventTabs.Fulfillment,
      label: <Content id={ContentId.Delivery} />,
      contentId: ContentId.Delivery,
      Icon: DeliveryIcon,
      content: (
        <PurchaseEventFulfillmentSection
          accordionItemProps={accordionItemProps}
          purchaseOrderAccordionItem={purchaseOrderAccordionItem}
        />
      ),
    });
  }

  if (!event) {
    if (
      quickFilterSearchParams ||
      getAppliedFilterCounts(tempQuery, initialQuery)
    )
      return (
        <EmptySectionContent.Root
          icon={
            <EmptySectionContent.SolidIconContainer>
              <SearchSolidIcon />
            </EmptySectionContent.SolidIconContainer>
          }
        >
          <EmptySectionContent.Label>
            <Content id={ContentId.NoResultFound} />
          </EmptySectionContent.Label>
          <EmptySectionContent.DetailMessage>
            <Content id={ContentId.NoFilteredNotifications} />
          </EmptySectionContent.DetailMessage>
        </EmptySectionContent.Root>
      );
  }

  if (isLoading || !event) {
    return <PosSpinner />;
  }

  return (
    <div
      className={clsx(styles.root, {
        [styles.moibleRoot]: isMobile,
      })}
    >
      <ExplorerLayout
        swiperRef={swiperRef}
        defaultRatio={0.35}
        maxPanelPercent={0.5}
        showExpandButton
        sidePanelPage={SidePanelPage.PurchaseEvent}
        rightPanelContent={
          <SidePanel
            event={event}
            pageType="Purchases"
            selectedEntityIds={selectedPurchaseOrderIds}
            filterQuery={{
              ...filterQuery,
              marketplaceEntityIds:
                filterQuery.marketplaceEntityIds &&
                filterQuery.marketplaceEntityIds?.length > 0
                  ? poIds
                  : null, // We have to pass in poIds cause we use tktGrpIds for filtering since this is a purchase event
            }}
          />
        }
      >
        <div className={styles.leftContainer}>
          <CollapsableViewProvider isEnabled={true}>
            <PurchaseEventPageHeaderV2
              event={event}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              tabs={tabs}
              defaultTab={defaultTab}
              onUpdateDefaultTab={onSetDefaultTabClick}
            />
          </CollapsableViewProvider>
        </div>
      </ExplorerLayout>
    </div>
  );
}
